import {
  useAsyncData,
  useHead,
  useRoute,
  useRouter,
  useRuntimeConfig,
  useState,
} from '#imports'
import { computed } from 'vue'
import type { Router } from 'vue-router'

import type {
  MenuItem,
  Service,
} from '@backmarket/http-api/src/api-specs-navigation-experience/navigation/sidebar'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import type { Logger, Owner } from '@backmarket/nuxt-module-logger/types'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import { isBlackFridaySeason, isHolidaysSeason } from '../utils/seasonsHelpers'

import t from './useNavigation.translations'

export interface Item {
  children: Item[] | never[]
  color?: string
  icon?: string
  id: string
  image?: string
  title: string
  url: string
  details?: Service[]
}

enum ItemType {
  CATEGORY = 'category',
  EVENT = 'event',
  GIFT = 'gift',
  UNIVERSE = 'universe',
}

function toUrl(locale: string, name: string, id: string, type: string): string {
  if (type.toLowerCase() === ItemType.EVENT) {
    return `/${locale}/e/${name}`
  }
  if (
    type.toLowerCase() === ItemType.UNIVERSE ||
    type.toLowerCase() === ItemType.CATEGORY
  ) {
    return `/${locale}/l/${name}/${id}`
  }

  if (type.toLowerCase() === ItemType.GIFT && name === 'home') {
    return `/${locale}/${type}`
  }

  return `/${locale}/${type}/${name}`
}

function mapMenuItemtoItem(
  item: MenuItem,
  router: Router,
  logger: Logger<Owner[]>,
  locale: string,
): Item {
  return {
    children:
      item.children?.map((elt) =>
        mapMenuItemtoItem(elt, router, logger, locale),
      ) || [],
    id: item.id,
    image: item.imageUrl,
    title: item.title,
    url: toUrl(locale, item.name, item.id, item.type),
    details: item.meta?.map((meta) => meta.sidebar),
    icon: item.icon,
    color: item.id === 'good-deals' ? '#9D3963' : undefined,
  }
}

export function useNavigation() {
  const $httpFetch = useHttpFetch()
  const router = useRouter()
  const route = useRoute()
  const logger = useLogger()
  const { trackClick } = useTracking()
  const currentLocale = useI18nLocale().toLowerCase()

  const { data: navigationData } = useAsyncData(
    'navigation-main-menu',
    () => $httpFetch('/bm/navigation/main'),
    { dedupe: 'defer' },
  )

  const mappedItems = computed(() => {
    if (!navigationData.value) return []

    return navigationData.value.items.map((item) =>
      mapMenuItemtoItem(item, router, logger, currentLocale),
    )
  })

  const i18n = useI18n()
  const locale = useI18nLocale().toLowerCase()

  const {
    features: {
      goodDeals: { enabled: isGoodDealsEnabled } = { enabled: false },
      blackFriday: { enabled: isBlackFridayEnabled } = { enabled: false },
      holidays: { enabled: isHolidaysEnabled } = { enabled: false },
    } = {},
  }: {
    features?: {
      goodDeals?: { enabled: boolean }
      blackFriday?: { enabled: boolean }
      holidays?: { enabled: boolean }
    }
  } = useMarketplace() ?? {}

  const { FF_ENABLE_NAVIGATION_ITEMS_BY_SERVICE = false } =
    useRuntimeConfig().public

  const computedItems = computed(() => {
    const GOOD_DEALS: Item = {
      title: i18n(t.goodDeals),
      children: [],
      id: 'good-deals',
      color: '#9D3963',
      icon: 'Sparkles',
      url: `/${locale}/e/good-deals`,
    }

    const BLACK_FRIDAY: Item = {
      title: 'Black Friday',
      children: [],
      id: 'black-friday',
      color: '#9D3963',
      icon: 'Sparkles',
      url: `/${locale}/e/black-friday`,
    }

    const HOLIDAYS: Item = {
      title: i18n(t.holidays),
      children: [],
      id: 'holidays',
      color: '#9D3963',
      icon: 'Sparkles',
      url: `/${locale}/e/holidays`,
    }

    if (FF_ENABLE_NAVIGATION_ITEMS_BY_SERVICE) {
      return mappedItems.value
    }

    return [GOOD_DEALS, BLACK_FRIDAY, HOLIDAYS]
      .concat(mappedItems.value)
      .filter((item) => {
        const now = new Date()
        if (item.id === 'black-friday') {
          return (
            isGoodDealsEnabled &&
            isBlackFridayEnabled &&
            isBlackFridaySeason(now)
          )
        }
        if (item.id === 'holidays') {
          return (
            isGoodDealsEnabled && isHolidaysEnabled && isHolidaysSeason(now)
          )
        }
        if (item.id === 'good-deals') {
          return (
            isGoodDealsEnabled &&
            !isHolidaysSeason(now) &&
            !isBlackFridaySeason(now)
          )
        }

        return true
      })
  })

  const activeMenu = useState<string | undefined>(
    'menu-active',
    () => undefined,
  )
  const isMenuActive = computed(() => activeMenu.value !== undefined)
  const setActiveMenu = (id: string | undefined) => {
    activeMenu.value = id
  }
  const clearActiveMenu = () => {
    activeMenu.value = undefined
  }

  const isOpen = useState('burger-menu-isOpen', () => false)
  useHead({
    bodyAttrs: {
      class: computed(() =>
        isOpen.value ? 'overflow-hidden lg:overflow-auto' : '',
      ),
    },
  })
  const state = computed(() => (isOpen.value ? 'open' : 'closed'))

  const close = () => {
    clearActiveMenu()

    isOpen.value = false
  }

  const toggle = () => {
    clearActiveMenu()

    isOpen.value = !isOpen.value
    if (isOpen.value) {
      trackClick({ zone: 'nav', name: 'all_product' })
    }
  }

  const isActivePage = (link: string | undefined) => {
    if (!link) return false
    try {
      const url = new URL(link, window.location.origin)

      return route.fullPath === url.pathname
    } catch (err) {
      return route.fullPath === link
    }
  }

  return {
    clearActiveMenu,
    setActiveMenu,
    toggle,
    isActivePage,
    activeMenu,
    isMenuActive,
    isOpen,
    items: computedItems,
    state,
    close,
  }
}
